import React from 'react';
import * as Sentry from '@sentry/react';
import logger from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error('Error boundary caught an error:', error, info);
    Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  handleRetry = () => {
    if (typeof this.props.onRetry === 'function') {
      this.props.onRetry();
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const title = this.props.title || 'Something went wrong';
      const description = this.props.description || 'Please try again or contact support if the issue persists.';

      return (
        <div className="error-boundary">
          <h2>{title}</h2>
          <p>{description}</p>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '12px',
              borderRadius: '8px',
              overflowX: 'auto',
              maxHeight: '240px',
            }}>
              {this.state.error?.stack || this.state.error?.message}
            </pre>
          )}
          <button className="quantum-btn" onClick={this.handleRetry}>
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export const withErrorBoundary = (Component, boundaryProps = {}) => {
  const Wrapped = (props) => (
    <ErrorBoundary {...boundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  Wrapped.displayName = `WithErrorBoundary(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};

export default ErrorBoundary;
